import { GLTFExporter } from "three/examples/jsm/Addons.js";
import {db, storage} from './firebaseconfig';
import {ref, uploadBytes, getDownloadURL} from 'firebase/storage';
import { collection, addDoc, doc, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import Experience from "../Experience";
import * as THREE from "three";

type SceneDataLike = Record<string, unknown> & {
    slug?: string;
    gltfURL?: string;
    elementsToRegenerate?: unknown;
};
type GltfExportScene = {
    extras?: SceneDataLike;
};
type GltfExportResult = ArrayBuffer | { scenes?: GltfExportScene[] };

export default class ExportPath {
    experience: Experience;
    canvas: HTMLCanvasElement | null;
    scene: THREE.Scene;
    constructor(){
        this.experience = new Experience()
        this.canvas = this.experience.canvas
        this.scene = this.experience.scene
    }
    async exportGLTF(tempScene: THREE.Scene)
    {
        const gltfExporter = new GLTFExporter();
        const options = {
            // trs: params.trs,
            // onlyVisible: params.onlyVisible,
            // binary: params.binary,
            // maxTextureSize: params.maxTextureSize
        };
        gltfExporter.parse(tempScene, async (result: GltfExportResult) => {
            if (result instanceof ArrayBuffer) {
                console.error("GLTF export returned binary result without scene extras");
                return;
            }
            const sceneData = result.scenes?.[0]?.extras;
            const slug = sceneData?.slug;
            if (!sceneData || !slug) {
                console.error("Missing scene metadata in GLTF export result");
                return;
            }
            const elementsToRegenerate = sceneData.elementsToRegenerate;

            if ( result instanceof ArrayBuffer ) {
                const gltfBlob = new Blob([result], { type: 'application/octet-stream' });
                const gltfFileName = `${slug}.glb`;
                const gltfURL = await this.uploadGLTF(gltfBlob, gltfFileName);
                sceneData.gltfURL = gltfURL;
            } else {
                const gltfString = JSON.stringify(result, null, 2);
                const gltfBlob = new Blob([gltfString], { type: 'application/json' });
                const gltfFileName = `${slug}.gltf`;
                const gltfURL = await this.uploadGLTF(gltfBlob, gltfFileName);
                sceneData.gltfURL = gltfURL;
            }
            const elementsToRegenerateBlob = new Blob([JSON.stringify(elementsToRegenerate)], { type: 'application/json' });
            const elementsToRegenerateFileName = `${slug}_elementsToRegenerate.json`;
            const elementsToRegenerateURL = await this.uploadSceneData(elementsToRegenerateBlob, elementsToRegenerateFileName);

            if (!this.experience.sceneDocId) {
                await this.createNewSceneEntry(sceneData, elementsToRegenerateURL);
            }
        // Assume there's a canvas element available for screenshot capture
            if (this.canvas) {
                this.experience.update()
                await this.captureScreenshotAndSaveMetadata(this.canvas, sceneData, elementsToRegenerateURL);
            } else {
                console.error('Canvas element not found');
            }
        }, (error: unknown) => {
            console.error('An error happened during parsing', error);
        }, options);
    }
    async uploadSceneData(blob: Blob, filename: string) {
        const storageRef = ref(storage, `scene_data/${filename}`);
        try {
            const snapshot = await uploadBytes(storageRef, blob);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error('Scene data upload failed', error);
            throw error;
        }
    }
    async uploadGLTF(blob: Blob, filename: string) {
        const storageRef = ref(storage, `gltf_files/${filename}`);
        try {
        const snapshot = await uploadBytes(storageRef, blob);
        return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error('GLTF upload failed', error);
            throw error;
        }
    }
    // Function to capture the screenshot, upload it, and then save both GLTF and screenshot metadata
    async captureScreenshotAndSaveMetadata (canvas: HTMLCanvasElement, sceneData: SceneDataLike, elementsToRegenerateURL: string)
    {
        canvas.toBlob(async (blob) => {
            if (!blob) return;
            const screenshotFileName = `${sceneData.slug}-${canvas.width}x${canvas.height}.jpg`;
            const screenshotURL = await this.uploadScreenshot(blob, screenshotFileName);
            // this.downloadScreenshot(blob, screenshotFileName)
            await this.saveGLTFWithScreenshot(sceneData, elementsToRegenerateURL, screenshotURL);
        });
    }
    downloadScreenshot(blob: Blob, filename: string)
    {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }
    // Uploads the screenshot blob to Firebase Storage and saves metadata to Firestore
    async uploadScreenshot (blob: Blob, fileName: string)
    {
        const storageRef = ref(storage, `screenshots/${fileName}`);
        try {
            const snapshot = await uploadBytes(storageRef, blob);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error('Screenshot upload failed', error);
            throw error
        }
    }
    // Saves both GLTF metadata and screenshot URL to Firestore in a single document
    async saveGLTFWithScreenshot(sceneData: SceneDataLike, elementsToRegenerateURL: string, screenshotURL: string)
    {
        const { elementsToRegenerate, ...restSceneData } = sceneData;

        try {
            if (!this.experience.sceneDocId) {
                return;
            }
            const docRef = doc(db, 'scene_metadata', this.experience.sceneDocId)
            await updateDoc(docRef, {
                ...restSceneData,
                elementsToRegenerateURL,
                screenshotURL: screenshotURL,
                isTemporary: false,
                updatedAt: new Date()
            }); 
            if (this.experience.saveTimeout) {
                clearTimeout(this.experience.saveTimeout);
                this.experience.saveTimeout = null;
            }
            // console.log('Document updated with ID: ', this.experience.sceneDocId);
            if (this.experience.uiHandlers?.saveBtn) {
                this.experience.uiHandlers.saveBtn.textContent = "Saved!"
                setTimeout(()=>{
                    this.experience.uiHandlers.saveBtn.classList.add('disabled')
                    this.experience.uiHandlers.saveBtn.textContent = "Save"
                    this.experience.uiHandlers.saveBtn.style.color = this.experience.uiHandlers.themeTextColor || ""
                },2000)
            }
        } catch (error) {
            console.error('Error adding document: ', error);
        }
    }
    async createNewSceneEntry(sceneData: SceneDataLike, elementsToRegenerateURL: string) {
        const { elementsToRegenerate, ...restSceneData } = sceneData;
        // console.log(elementsToRegenerateURL);
        try {
            const docRef = await addDoc(collection(db, 'scene_metadata'), {
                ...restSceneData,
                elementsToRegenerateURL: elementsToRegenerateURL,
                isTemporary: true,
                createdAt: new Date()
            });
            this.experience.sceneDocId = docRef.id;
            this.experience.scene.userData.docID = docRef.id
            // console.log('New scene document created with ID: ', docRef.id);
            this.experience.saveTimeout = setTimeout(async () => {
                await this.checkAndDeleteIfNotSaved();
            }, 300000); // 300000 ms = 5 minutes

        } catch (error) {
            console.error('Error creating new scene document: ', error);
        }
    }
    async checkAndDeleteIfNotSaved() {
        try {
            if(this.experience.sceneDocId !== null){
                const idToDelete = this.experience.sceneDocId
                const docRef = doc(db, 'scene_metadata', this.experience.sceneDocId);
                const docSnapshot = await getDoc(docRef);
                if (docSnapshot.exists() && docSnapshot.data().isTemporary) {
                    await deleteDoc(docRef);
                    // console.log('Temporary document deleted with ID: ', idToDelete);
                }
            }
        } catch (error) {
            console.error('Error checking and deleting document: ', error);
        }
    }
    // Duplicate the scene when importing
    async duplicateScene(existingSceneId: string) {
        try {
            const existingDocRef = doc(db, 'scene_metadata', existingSceneId);
            const existingDocSnapshot = await getDoc(existingDocRef);
            if (existingDocSnapshot.exists()) {
                const existingData = existingDocSnapshot.data();
                const newDocRef = await addDoc(collection(db, 'scene_metadata'), {
                    ...existingData,
                    createdAt: new Date(),
                    isTemporary: true,
                    forkID: existingSceneId
                });
                this.experience.sceneDocId = newDocRef.id
                this.experience.saveTimeout = setTimeout(async () => {
                    await this.checkAndDeleteIfNotSaved();
                }, 300000); // 300000 ms = 5 minutes
                // console.log('Document duplicated with new ID: ', newDocRef.id);
                return newDocRef.id;
            } else {
                console.error('No such document to duplicate!');
            }
        } catch (error) {
            console.error('Error duplicating document: ', error);
        }
    }
}
